import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { BookOpen, CheckCircle, Clock, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import CategoryFilter from '../components/CategoryFilter';

const MyCoursesPage = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMyCourses();
  }, [selectedCategory]);

  const loadMyCourses = async () => {
    try {
      const params = selectedCategory ? `?category=${selectedCategory}` : '';
      const response = await api.get(`/my-courses${params}`);
      setCourses(response.data);

      // Only update categories from full (unfiltered) list
      if (!selectedCategory) {
        const uniqueCategories = [...new Set(response.data.map((c) => c.category).filter(Boolean))];
        setCategories(uniqueCategories);
      }
    } catch (error) {
      console.error('Failed to load my courses:', error);
      toast.error('فشل تحميل مساقاتي');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg text-accent">جارٍ التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="my-courses-page">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-accent mb-4">منهجيتي</h1>
        <p className="text-lg text-muted-foreground">المساقات التي انضممت إليها</p>
      </div>

      {/* Category Filter */}
      {categories.length > 0 && (
        <CategoryFilter
          categories={categories}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
        />
      )}

      {/* Courses */}
      {courses.length === 0 ? (
        <div className="text-center py-16">
          <BookOpen className="w-16 h-16 text-muted mx-auto mb-4 opacity-50" />
          <p className="text-lg text-muted-foreground mb-4">
            لم تنضم إلى أي مساق بعد
          </p>
          <Button onClick={() => (window.location.href = '/courses')} className="bg-primary">
            تصفح المساقات المتاحة
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {courses.map((course) => (
            <Card key={course.course_id} className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
              <div className="grid md:grid-cols-4 gap-0">
                <div
                  className="h-48 md:h-auto bg-cover bg-center"
                  style={{
                    backgroundImage: `url('${course.image_url || 'https://images.unsplash.com/photo-1609599006353-e629aaabfeae?auto=format&fit=crop&q=80&w=800'}')`,
                  }}
                ></div>
                <div className="md:col-span-3 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <span className="inline-block bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium mb-3">
                        {course.category}
                      </span>
                      <h3 className="text-2xl font-bold text-accent mb-2">{course.title}</h3>
                      <p className="text-muted-foreground">{course.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {course.status === 'completed' ? (
                        <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 rounded-full text-xs font-bold" data-testid={`course-badge-${course.course_id}`}>
                          <CheckCircle className="w-4 h-4" /> مكتمل
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-700 border border-amber-200 px-3 py-1 rounded-full text-xs font-bold" data-testid={`course-badge-${course.course_id}`}>
                          <Clock className="w-4 h-4" /> جاري
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                    <span>تاريخ الانضمام: {course.enrollment_date ? new Date(Number(course.enrollment_date) || course.enrollment_date).toLocaleDateString('ar') : '-'}</span>
                  </div>

                  <Button className="bg-primary hover:bg-primary/90" onClick={() => navigate(`/courses/${course.course_id}`)}>
                    <ExternalLink className="w-5 h-5 ml-2" />
                    عرض التفاصيل والكتب
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyCoursesPage;
